'use client';

import { getCredentialsAndIdentityId } from '@/libs/amplify/get-credentials-and-identity-id';
import { trackSignup } from '@/libs/firstpromoter';
import {
  rudderanalyticsIdentify,
  RudderanalyticsIdentifyInput
} from '@/libs/rudderstack';
import { journalStore } from '@/stores/journal-store';
import { playerStore } from '@/stores/player-store';
import { AnalyticsProps } from '@/types';
import { getCallbackRedirectUrl } from '@/utils/redirect';
import {
  CredentialsAndIdentityId,
  GetCredentialsOptions
} from '@aws-amplify/core';
import axios from 'axios';
import isNil from 'lodash-es/isNil';
import omit from 'lodash-es/omit';
import omitBy from 'lodash-es/omitBy';
import { Session } from 'next-auth';
import {
  getSession,
  signIn,
  SignInResponse,
  signOut,
  UpdateSession
} from 'next-auth/react';
import { createStore } from 'zustand-x';

/**
 * Represents the structure of the authentication store.
 */
export type AuthStore = {
  /** The current user session or null if not authenticated. */
  session: Session | null;
  /** AWS Amplify credentials and identity ID. */
  credentials: CredentialsAndIdentityId | null;
};

export type SignInBaseInput = {
  redirectUrl?: string;
  analyticsProps?: AnalyticsProps;
};

export type SignInCredentialsInput = SignInBaseInput & {
  email: string;
  password: string;
};

export type SignInTokensInput = SignInBaseInput & {
  accessToken: string;
  refreshToken: string;
  idToken: string;
};

export type SignInInput = SignInCredentialsInput | SignInTokensInput;

export type SignUpInput = {
  email: string;
  password?: string | null;
  locale: string;
  captcha?: string;
  isRecaptchaEnabled?: boolean;
  redirectUrl?: string;
  analyticsProps?: AnalyticsProps;
};

function isTokensInput(input: SignInInput): input is SignInTokensInput {
  return (
    'accessToken' in input && 'refreshToken' in input && 'idToken' in input
  );
}

let cachedCredentialsPromise: Promise<
  CredentialsAndIdentityId | undefined
> | null = null;

let cachedSessionPromise: Promise<Session | null> | null = null;

/**
 * Defines `authStore` for managing authentication state across the application.
 * This store incorporates AWS Amplify's authentication functionalities to handle user
 * authentication states and loading indicators. It's designed to be isomorphic,
 * facilitating seamless integration in both server-side and client-side environments.
 *
 * State:
 * - `session`: Stores the authenticated user's session information or `null` if no user is signed in.
 * - `credentials`: Stores the amplify credentials and identity id for internal use.
 *
 * Selectors:
 * - `session()`: Retrieves the current user session.
 * - `user()`: Retrieves the current user state.
 * - `userId()`: Retrieves the current user id.
 * - `isSignedIn()`: Determines if a user is currently signed in.
 *
 * Actions:
 * - `signIn`: Authenticates a user with username and password.
 * - `signOut`: Signs out the current user.
 * - `signUp`: Registers a new user.
 * - `reset`: Resets the authentication state.
 *
 * Usage Guidelines:
 * - Direct access to the store's state is discouraged to avoid state hydration issues on the first render, especially in SSR contexts.
 * - Prefer using extended selectors for accessing state to ensure that the hydrated state from the server is accurately reflected on the client during the initial render.
 *
 * Good Practices:
 * - `authStore.useTracked.isSignedIn()`, `authStore.useTracked.user()` are recommended for getting updated sign-in state and user information, minimizing unnecessary re-renders.
 * - For server-side usage, prefer `authStore.get.user()`, `authStore.get.isSignedIn()` to directly access the current user or loading state without subscribing to state changes. This approach is suitable for server-side rendering (SSR) scenarios where reactivity is not required.
 *
 * Potential Pitfalls:
 * - Using `authStore.use.user()` might lead to excessive re-renders due to state changes that are irrelevant to the consuming component.
 *
 */
export function createAuthStore(
  session: Session | null,
  refreshSession: UpdateSession
) {
  return createStore('auth')<AuthStore>({
    session,
    credentials: null
  })
    .extendSelectors((_state, get) => ({
      /**
       * Retrieves the current user from the session.
       * @returns The current user or undefined if not signed in.
       */
      user() {
        return get.session()?.user;
      },

      /**
       * Determines if a user is currently signed in.
       * @returns `true` if a user is signed in, otherwise `false`.
       */
      isSignedIn() {
        return !!get.session();
      }
    }))
    .extendSelectors((_state, get) => ({
      /**
       * Retrieves the current user ID.
       * @returns The user ID or undefined if not signed in.
       */
      userId() {
        return get.user()?.sub;
      }
    }))
    .extendActions((set) => ({
      /**
       * Refreshes the session
       */
      refreshSession: async () => {
        if (!cachedSessionPromise) {
          cachedSessionPromise = refreshSession({});
        }
        const newSession = await cachedSessionPromise;
        cachedSessionPromise = null;

        if (newSession) {
          set.session(newSession);
        }
        return newSession;
      },
      /**
       * Refreshes the credentials
       */
      refreshCredentials: async (input: GetCredentialsOptions) => {
        let newCredentials: CredentialsAndIdentityId | undefined;
        if (typeof window === 'undefined') {
          newCredentials = await getCredentialsAndIdentityId(
            input.tokens?.idToken?.toString()
          );
        } else {
          if (!cachedCredentialsPromise) {
            cachedCredentialsPromise = axios
              .post('/api/auth/refresh-credentials')
              .then((res) => res.data);
          }
          newCredentials = await cachedCredentialsPromise;
          if (newCredentials?.credentials?.expiration) {
            newCredentials.credentials.expiration = new Date(
              newCredentials.credentials.expiration
            );
          }
          cachedCredentialsPromise = null;
        }

        if (newCredentials) {
          set.credentials(newCredentials);
        }
        return newCredentials;
      },
      /**
       * Resets the session state.
       */
      resetSession() {
        cachedSessionPromise = null;
        set.session(null);
      },
      /**
       * Resets the credentials state.
       */
      resetCredentials() {
        cachedCredentialsPromise = null;
        set.credentials(null);
      }
    }))
    .extendActions((set) => ({
      /**
       * Resets the authentication state.
       */
      reset() {
        set.resetSession();
        set.resetCredentials();
      },

      async postAuthCallback({
        analyticsProps,
        disableRedirect,
        redirectUrl,
        traits
      }: {
        analyticsProps?: AnalyticsProps;
        disableRedirect?: boolean;
        redirectUrl?: string;
        traits?: RudderanalyticsIdentifyInput;
      }) {
        const session = await getSession();
        if (session?.user) {
          rudderanalyticsIdentify({
            ...session.user,
            ...traits
          });
        }
        set.session(session);
        set.resetCredentials();
        if (session?.user?.email) {
          await trackSignup(session.user.email, analyticsProps);
        }
        if (!disableRedirect) {
          const url = redirectUrl || getCallbackRedirectUrl();
          window.location.href = url.toString();
        }
        return session;
      }
    }))
    .extendActions((set) => ({
      /**
       * Registers a new user with the provided sign-up input.
       *
       * Steps:
       * 1. Retrieves the redirect URL from the `redirect-url` cookie.
       * 2. Calls the `signUp` function from `aws-amplify/auth` with the provided input.
       * 3. Tracks the signup event using `trackSignup`.
       * 4. Checks if the next step in the signup process is `COMPLETE_AUTO_SIGN_IN`.
       * 5. If auto sign-in is complete, attempts to sign in the user with the provided input (omitting options).
       * 6. If sign-in is successful, sets the redirect URL to '/'.
       * 7. If sign-in fails, sets the redirect URL to '/sign-in'.
       * 8. Redirects the user to the determined redirect URL.
       * 9. If the signup step is not `COMPLETE_AUTO_SIGN_IN`, throws an error indicating signup failed.
       *
       * @param input - The sign-up input data.
       * @param disableRedirect - Disable redirection after signing up
       * @throws Will throw an error if signup fails.
       */
      async signUp(input: SignUpInput, disableRedirect?: boolean) {
        const zoneinfo = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const signupInput = omitBy(
          {
            email: input.email,
            password: input.password,
            locale: input.locale,
            captcha: input.captcha,
            isRecaptchaEnabled: input.isRecaptchaEnabled,
            zoneinfo,
            redirect: false
          },
          isNil
        );
        const response = (await signIn(
          'credentials-sign-up',
          signupInput
        )) as unknown as SignInResponse;

        if (response?.error) {
          throw new Error(response?.code || 'signup failed');
        }

        return set.postAuthCallback({
          analyticsProps: input.analyticsProps,
          redirectUrl: input.redirectUrl,
          disableRedirect,
          traits: {
            'Sign Up Date': new Date().toISOString()
          }
        });
      },

      /**
       * Authenticates a user with the provided sign-in options.
       * @param input - The sign-in options.
       * @param disableRedirect - Disable redirection props.
       * @throws Will throw an error if login fails.
       */
      async signIn(input: SignInInput, disableRedirect?: boolean) {
        const provider = isTokensInput(input)
          ? 'credentials-token'
          : 'credentials-sign-in';

        const response = await signIn(provider, {
          ...omit(input, 'redirectUrl', 'analyticsProps'),
          redirect: false
        });
        if (response?.error) {
          throw new Error(response?.code || 'login failed');
        }
        return set.postAuthCallback({
          analyticsProps: input.analyticsProps,
          redirectUrl: input.redirectUrl,
          disableRedirect
        });
      },

      /**
       * Signs out the current user.
       *
       * Steps:
       * 1. Calls the `signOut` function from `next-auth/react` and prevents redirect.
       * 2. Tracks the sign-out event using an analytics tool.
       * 3. Resets the analytics data.
       * 4. Resets the authentication state.
       * 5. Resets the state of `journalStore` and `playerStore`.
       * 6. Redirects the user to the sign-in page.
       */
      async signOut(disableRedirect?: boolean) {
        try {
          await signOut({ redirect: false });
          rudderanalytics.track('Sign Out');
          rudderanalytics.reset(true);
        } catch (e) {
          // Handle sign out error if necessary
        }

        set.reset();
        journalStore.set.reset();
        playerStore.set.reset();
        if (!disableRedirect) {
          const currentUrl = new URL(window.location.href);
          const redirectUrl = new URL('/sign-in', window.location.origin);
          const callbackUrl = currentUrl.searchParams.get('callbackUrl');

          if (callbackUrl) {
            redirectUrl.searchParams.set('callbackUrl', callbackUrl);
          }

          window.location.href = redirectUrl.toString();
        }
      }
    }));
}
